import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, MapPin, Pencil, Check, X, Loader2, Send } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { JuenWordmark } from "@/components/juen-wordmark";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/tickets/$ticketId")({
  head: () => ({ meta: [{ title: "Bilet — JUËN" }] }),
  component: TicketDetailPage,
});

interface TicketDetail {
  id: string;
  code: string;
  created_at: string;
  checked_in_at: string | null;
  attendee_name: string | null;
  events: {
    id: string;
    name: string;
    starts_at: string;
    ends_at: string | null;
    venue: string | null;
    cover_image_url: string | null;
    description: string | null;
  } | null;
  ticket_types: { name: string; description: string | null } | null;
}

function formatRange(starts: string, ends: string | null, locale: string): string {
  const s = new Date(starts);
  const startStr = s.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  if (!ends) return startStr;
  const e = new Date(ends);
  const endStr = e.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return `${startStr} — ${endStr}`;
}

function TicketDetailPage() {
  const { ticketId } = Route.useParams();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferring, setTransferring] = useState(false);
  const tc = useAutoTranslate([ticket?.events?.name, ticket?.events?.venue, ticket?.ticket_types?.name]);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data } = await supabase
        .from("tickets")
        .select(
          "id,code,created_at,checked_in_at,attendee_name,events(id,name,starts_at,ends_at,venue,cover_image_url,description),ticket_types(name,description)",
        )
        .eq("id", ticketId)
        .maybeSingle();
      setTicket((data as unknown as TicketDetail) ?? null);
      setPageLoading(false);
    })();
  }, [ticketId, user]);

  const handleSaveName = async () => {
    if (!ticket) return;
    setSavingName(true);
    const trimmed = nameInput.trim();
    const { error } = await supabase.rpc("set_ticket_attendee_name", {
      _ticket_id: ticket.id,
      _name: trimmed,
    });
    setSavingName(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setTicket({ ...ticket, attendee_name: trimmed || null });
    setEditingName(false);
    toast.success(t("account.ticketDetail.nameUpdated"));
  };

  const handleTransfer = async () => {
    if (!ticket) return;
    const email = transferEmail.trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("account.emailInvalid"));
      return;
    }
    setTransferring(true);
    const { data, error } = await supabase.rpc("transfer_ticket", {
      _ticket_id: ticket.id,
      _recipient_email: email,
    });
    setTransferring(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const recipientName = (data as { recipient_name?: string } | null)?.recipient_name;
    toast.success(
      recipientName
        ? t("account.ticketDetail.transferSuccessNamed", { name: recipientName })
        : t("account.ticketDetail.transferSuccess"),
    );
    setTransferOpen(false);
    void navigate({ to: "/tickets" });
  };

  useEffect(() => {
    if (!ticket) return;
    void QRCode.toDataURL(ticket.code, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 480,
      color: { dark: "#000000", light: "#ffffff" },
    }).then(setQrDataUrl);
  }, [ticket]);

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!ticket || !ticket.events) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("account.ticketDetail.notFound")}</h1>
          <Button asChild className="mt-4">
            <Link to="/tickets">{t("account.ticketDetail.backToTickets")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const buyerName = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim() || (user.email ?? "");
  const displayName = ticket.attendee_name?.trim() || buyerName;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <Link to="/tickets" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t("account.tickets.title")}
          </Link>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            {t("account.ticketDetail.print")}
          </Button>
        </div>

        {/* Attendee name editor (hidden in print) */}
        <div className="mb-4 rounded-2xl border border-border bg-card p-4 shadow-card print:hidden">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("account.ticketDetail.holder")}</div>
              {!editingName ? (
                <div className="mt-1 truncate text-base font-semibold">{displayName}</div>
              ) : (
                <Input
                  autoFocus
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder={t("account.ticketDetail.namePlaceholder")}
                  maxLength={80}
                  className="mt-1"
                />
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {ticket.checked_in_at
                  ? t("account.ticketDetail.nameLockedHint")
                  : t("account.ticketDetail.nameHint")}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              {!editingName ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!!ticket.checked_in_at}
                  onClick={() => {
                    setNameInput(ticket.attendee_name ?? "");
                    setEditingName(true);
                  }}
                >
                  <Pencil className="mr-1 h-4 w-4" /> {t("account.ticketDetail.edit")}
                </Button>
              ) : (
                <>
                  <Button size="sm" onClick={() => void handleSaveName()} disabled={savingName}>
                    {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingName(false)} disabled={savingName}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
          {!ticket.checked_in_at && (
            <div className="mt-3 border-t border-border pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTransferEmail("");
                  setTransferOpen(true);
                }}
              >
                <Send className="mr-1.5 h-4 w-4" /> {t("account.ticketDetail.transferButton")}
              </Button>
            </div>
          )}
        </div>

        <AlertDialog open={transferOpen} onOpenChange={setTransferOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("account.ticketDetail.transferTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("account.ticketDetail.transferDesc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              type="email"
              placeholder={t("account.ticketDetail.emailPlaceholder")}
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              maxLength={255}
              autoFocus
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={transferring}>{t("account.giveUp")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleTransfer();
                }}
                disabled={transferring}
              >
                {transferring ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.ticketDetail.transferConfirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Ticket card — designed like the eventix sample */}
        <div
          ref={printRef}
          className="ticket-print overflow-hidden rounded-3xl border border-border bg-white text-black shadow-card"
        >
          {/* Top bar: brand row */}
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-[200px_1fr_220px] md:gap-8 md:p-8">
            {/* Left: brand + QR */}
            <div className="flex flex-col items-start">
              <JuenWordmark className="h-7 text-black" />
              <div className="mt-3 font-mono text-[11px] tracking-wider text-black/70">
                {ticket.code}
              </div>
              <div className="mt-3 rounded-md bg-white p-1">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt={`QR: ${ticket.code}`} className="h-44 w-44" />
                ) : (
                  <div className="h-44 w-44 animate-pulse bg-black/5" />
                )}
              </div>
              <div className="mt-3">
                <div className="text-base font-bold">{displayName}</div>
                <div className="text-xs text-black/60">{user.email}</div>
              </div>
            </div>

            {/* Middle: event info */}
            <div className="min-w-0">
              <h1 className="text-2xl font-extrabold leading-tight md:text-3xl">{tc(ticket.events.name)}</h1>
              {ticket.ticket_types && (
                <div className="mt-2 text-sm font-bold uppercase tracking-wide">{tc(ticket.ticket_types.name)}</div>
              )}
              <div className="mt-3 space-y-1.5 text-sm text-black/80">
                <div className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {formatRange(ticket.events.starts_at, ticket.events.ends_at, locale)}
                </div>
                {ticket.events.venue && (
                  <div className="flex items-start gap-1.5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{tc(ticket.events.venue)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: booking info */}
            <div className="rounded-lg border border-black/15 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-black/60">{t("account.ticketDetail.info")}</div>
              <div className="mt-2 space-y-1.5 text-sm">
                <div className="flex justify-between gap-2 border-b border-black/10 pb-1">
                  <span className="text-black/60">{t("account.ticketDetail.nameLabel")}</span>
                  <span className="text-right font-medium">{displayName}</span>
                </div>
                {profile?.city && (
                  <div className="flex justify-between gap-2 border-b border-black/10 pb-1">
                    <span className="text-black/60">{t("account.profile.city")}</span>
                    <span className="text-right font-medium">{profile.city}</span>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <span className="text-black/60">{t("account.ticketDetail.status")}</span>
                  <span className={`text-right font-medium ${ticket.checked_in_at ? "text-emerald-600" : "text-amber-600"}`}>
                    {ticket.checked_in_at ? t("account.ticketDetail.checkedIn") : t("account.ticketDetail.valid")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cover image */}
          {ticket.events.cover_image_url && (
            <div className="w-full overflow-hidden bg-black/5">
              <img
                src={ticket.events.cover_image_url}
                alt={ticket.events.name}
                className="h-auto w-full object-contain"
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-4 border-t border-black/10 px-6 py-4 md:px-8">
            <JuenWordmark className="h-5 text-black" />
            <p className="text-right text-[11px] text-black/60">
              {t("account.ticketDetail.footerLine1")}
              <br />
              {t("account.ticketDetail.footerLine2")}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          header, .print\\:hidden { display: none !important; }
          .ticket-print { box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </div>
  );
}
